import { useQuery, ApolloError } from "@apollo/client";
import { createContext, useEffect, useReducer, useState } from "react";
import useLocalStorageState from "use-local-storage-state";
import { GET_KURSUS_IDS, GET_PAGED_KURSUS_QUERY } from "../api/kursus/queries";
import { useTableSelection } from "../components/Table/useTableSelection";
import { defaultKursusColumns } from "../config/kursusConfig";
import useActiveColumns from "../hooks/useActiveColumns";
import { QueryFilter, KursusFilterKeys } from "../interfaces/filters";
import { PagedKursusContext } from "../interfaces/pagedContext";
import { queryFilterReducer } from "./reducers/queryFilterReducer";
import { buildFilter } from "../util/queryBuilder";
import { getActiveColumnFields } from "../util/table";
import {
  KursusFiltersContext,
  KursusFiltersEventContext,
} from "./FiltersContext";
import {
  IPagedKursusQuery,
  IPagedKursusResponse,
  PagedKursusQueryField,
} from "../interfaces/kursus";
import { IColumn, SortBy } from "../interfaces/table";
import { kursusIndexSortOrderKey } from "../util/localStorageKeys";

export const PagedKursusQueryContext = createContext<PagedKursusContext>({
  loading: false,
  error: undefined,
  columns: [],
  setActiveColumns: () => {},
  setQuery: () => {},
  page: 1,
  pagedResponse: undefined,
  nextPage: undefined,
  prevPage: undefined,
  refetch: undefined,
  selectAllError: undefined,
  clearSelectAllError: () => {},
  selection: {
    selectedIds: [],
    addSelectedIds: () => {},
    clearSelection: () => {},
    removeSelectedIds: () => {},
    selectAll: () => {},
    lastSelectedId: undefined,
    setLastSelectedId: () => {},
  },
});

export const PagedKursusQueryProvider = ({ children }) => {
  // type
  const type = "kursus";

  // state
  const [fetchAllSelected, setFetchAllSelected] = useState(false);
  const [selectAllTotalCount, setSelectAllTotalCount] = useState<number | null>(
    null,
  );
  const [selectAllError, setSelectAllError] = useState<ApolloError | undefined>(
    undefined,
  );
  const [query, setQuery] = useState<IPagedKursusQuery>({
    page: 1,
  });

  // localstorage state
  const [storedFilters, setStoredFilters] = useLocalStorageState<
    QueryFilter<KursusFilterKeys>[]
  >("kursusFilters", { defaultValue: [] });
  const [sortBy] = useLocalStorageState<Array<SortBy>>(
    kursusIndexSortOrderKey,
    { defaultValue: defaultSortBy },
  );

  // reducers
  const [queryFilters, updateFilters] = useReducer(
    queryFilterReducer,
    storedFilters,
  );

  // business
  const [columns, setActiveColumns] = useActiveColumns(
    "kursusindex-active-columns",
    defaultKursusColumns,
  );

  const {
    addSelectedIds,
    clearSelection,
    removeSelectedIds,
    selectAll,
    selectedIds,
    lastSelectedId,
    setLastSelectedId,
  } = useTableSelection();

  const page = query.page ? query.page : 1;
  const { after, before } = query;

  const {
    data,
    error: selectAllQueryError,
    loading: fetchAllLoading,
  } = useQuery<IPagedKursusResponse>(
    GET_KURSUS_IDS(query, selectAllTotalCount ?? 0),
    {
      skip: !fetchAllSelected || !selectAllTotalCount,
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    },
  );

  // effects
  useEffect(() => {
    setStoredFilters(queryFilters);
    setQuery({
      filters: buildFilter(queryFilters),
      order: {
        orderDirection: sortBy[0].direction.toUpperCase() as OrderDirection,
        orderKey: sortBy[0].field as PagedKursusQueryField,
      },
    });
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFilters, setQuery, setStoredFilters, sortBy]);

  useEffect(() => {
    if (!fetchAllSelected || fetchAllLoading || !data) return;

    const selectedIds = data.kursusholds.edges.map(
      (edge) => edge.node.id,
    ) as Array<string>;
    selectAll(selectedIds);
    setFetchAllSelected(false);
    setSelectAllTotalCount(null);
  }, [data, fetchAllLoading, fetchAllSelected, selectAll]);

  useEffect(() => {
    if (selectAllQueryError && fetchAllSelected) {
      setSelectAllError(selectAllQueryError);
      setFetchAllSelected(false);
      setSelectAllTotalCount(null);
    }
  }, [selectAllQueryError, fetchAllSelected]);

  // query
  const currentPage = usePagedKursusQuery(
    {
      after: after,
      before: before,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order,
    },
    columns,
  );

  const nextPage = usePagedKursusQuery(
    {
      after: currentPage.data?.kursusholds?.pageInfo.endCursor,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order,
    },
    columns,
  );

  const prevPage = usePagedKursusQuery(
    {
      before: currentPage.data?.kursusholds?.pageInfo.startCursor,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order,
    },
    columns,
  );
  const loading = currentPage.loading || nextPage.loading || prevPage.loading;

  return (
    <PagedKursusQueryContext.Provider
      value={{
        loading,
        error: currentPage.error,
        pagedResponse: currentPage?.data,
        columns,
        setActiveColumns,
        setQuery,
        query,
        page,
        nextPage,
        prevPage,
        refetch: currentPage.refetch,
        type,
        selectAllError,
        clearSelectAllError: () => setSelectAllError(undefined),
        selection: {
          addSelectedIds,
          clearSelection,
          removeSelectedIds,
          selectAll: (totalCount: number) => {
            setSelectAllError(undefined);
            setSelectAllTotalCount(totalCount);
            setFetchAllSelected(true);
          },
          selectedIds,
          lastSelectedId,
          setLastSelectedId,
        },
      }}
    >
      <KursusFiltersContext.Provider value={queryFilters}>
        <KursusFiltersEventContext.Provider value={updateFilters}>
          {children}
        </KursusFiltersEventContext.Provider>
      </KursusFiltersContext.Provider>
    </PagedKursusQueryContext.Provider>
  );
};

function usePagedKursusQuery(
  query: IPagedKursusQuery,
  columns: IColumn<PagedKursusQueryField>[],
) {
  return useQuery<IPagedKursusResponse>(
    GET_PAGED_KURSUS_QUERY({
      after: query.after,
      before: query.before,
      fields: getActiveColumnFields(columns),
      filters: query.filters,
      order: query.order,
    }),
  );
}

const defaultSortBy: Array<SortBy<PagedKursusQueryField>> = [
  {
    field: "fraDato",
    direction: "desc",
  },
];
